import React, { useState } from 'react'
import SoundOutDrill from './SoundOutDrill'
import JamoBlock from './JamoBlock'
import { decomposeWord, alignWord, soundOf, partLabel } from '../util/hangul'
import { KOR_WORDS } from '../data/drills/korWords'
import voices from '../speech/voices'
import { romanizationOf } from '../speech/flowMatch'

// "Sound it out" for Korean: each syllable block is drawn from its letters
// (JamoBlock) so the letters light up one by one. See SoundOutDrill for the
// drill itself; this file is the Korean plug-in and word list.

const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]] } return b }

// The curated words for the chosen syllable counts, plus anything the learner
// typed (comma / space separated Hangul, an optional "=meaning").
const buildItems = (lengths, custom, count) => {
  const own = String(custom || '').split(/[\s,，、]+/).map(s => s.trim()).filter(Boolean)
    .map(s => { const [k, e] = s.split('='); return { k: k.trim(), e: (e || '').trim() } })
    .filter(w => /[가-힣]/.test(w.k))
  const pool = shuffle((lengths || []).flatMap(n => (KOR_WORDS[n] || []).map(([k, e]) => ({ k, e }))))
  return [...own, ...pool].slice(0, Math.max(count || 10, own.length))
}

// JamoBlock takes states by role; the drill hands them by part index.
const Block = ({ syl, states, size, onPart }) => (
  <JamoBlock syl={syl} size={size}
    states={Object.fromEntries(syl.parts.map((p, pi) => [p.role, states[pi]]))}
    onPart={(p) => onPart(syl.parts.findIndex(x => x.role === p.role))} />
)

const plugin = {
  title: 'Sound it out · Korean',
  langCode: 'kor',
  voiceURI: voices['kor'] || voices['eng'],
  cps: 4.5,
  decompose: (item) => decomposeWord(item.k),
  align: (item, transcript) => alignWord(item.k, transcript),
  soundOf: (p) => soundOf(p),
  partLabel: (p) => partLabel(p),
  partText: (p) => p.jamo,
  syllableLabel: (syl) => `${syl.ch} · ${romanizationOf(syl.ch, 'kor') || ''}`,
  Block,
  // First guess before anything has been measured: Yuna says a syllable in
  // roughly 180 ms at rate 1. Split evenly across the syllable's letters; the
  // drill then scales the whole sweep to the measured length of the word.
  timing: (syl, rate) => { const per = 180 / rate / Math.max(1, syl.parts.length); return syl.parts.map(() => per) },
  pronunciation: (syls, item) => romanizationOf(item.k, 'kor') || '',
  legend: null,
  note: 'Tap a letter to hear its sound alone, a syllable to hear it whole. Don\'t chase a perfect score: if the word is recognisable, move on — reading speed matters more than a perfect ㅋ.',
  blockSize: (n) => (n > 3 ? 84 : n === 3 ? 108 : 128),
}

export default function KoreanSoundsView({ lengths = [1, 2], count = 10, custom = '', voiceSpeed, onBack, onClose }) {
  const [items] = useState(() => buildItems(lengths, custom, count))
  return <SoundOutDrill plugin={plugin} items={items} voiceSpeed={voiceSpeed} onBack={onBack} onClose={onClose} />
}
